import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { useCountry } from "../lib/country";
import { useLanguage } from "../lib/language";
import { useSiteMode } from "../components/SiteModeBanner";
import { api, formatPrice, formatApiErrorDetail, productImage } from "../lib/api";
import { toast } from "sonner";
import { CreditCard, Wallet, ArrowRightLeft, Store, User, MapPin, Truck, BadgeCheck, X } from "lucide-react";
import { generateReceipt } from "../lib/receipt";
import LocationPicker from "../components/LocationPicker";
import { centroidForDepartamento } from "../lib/svCentroids";

export default function Checkout() {
    const { items, total, originalTotal, siteDiscountAmount, clear, setQty, remove } = useCart();
    const { user, loading } = useAuth();
    const { country } = useCountry();
    const { t } = useLanguage();
    const siteMode = useSiteMode();
    const nav = useNavigate();
    const [method, setMethod] = useState("stripe");
    const [needsShipping, setNeedsShipping] = useState(false);
    const [tipoServicio, setTipoServicio] = useState("SER"); // SV-only: SER | CCE
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [loadingDeptos, setLoadingDeptos] = useState(false);
    const [loadingMunicipios, setLoadingMunicipios] = useState(false);
    const [form, setForm] = useState({
        customer_name: "", customer_lastname: "", customer_phone: "",
        // SV — structured fields (calle + depto + municipio + referencia) — backend recibe shipping_address compuesto
        sv_street: "",            // Calle y número de casa
        sv_reference: "",         // Puntos de referencia (opcional)
        shipping_address: "",     // composed string sent to backend
        departamento_id: "",
        municipio_id: "",
        // SV — GPS Plus Code (optional, complements the written address)
        location: null,           // { plus_code, plus_code_full, lat, lng, confirmed }
        // US — structured fields
        us_street: "", us_apt: "", us_city: "", us_state: "", us_zip: "",
        notes: "",
    });
    const [settings, setSettings] = useState({});
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    // Discount code state
    const [discountInput, setDiscountInput] = useState("");
    const [discountApplied, setDiscountApplied] = useState(null); // { code, name, type, value, amount_off }
    const [discountChecking, setDiscountChecking] = useState(false);

    // Partner code (afiliado / socio) — no descuento, solo tracking.
    const [partnerInput, setPartnerInput] = useState("");
    const [partnerApplied, setPartnerApplied] = useState(null); // { code, display_name, photo }
    const [partnerChecking, setPartnerChecking] = useState(false);

    // Wallet credit state
    const [useWalletCredit, setUseWalletCredit] = useState(false);
    const walletBalance = Number(user?.wallet_balance || 0);

    useEffect(() => { api.get("/settings").then(({ data }) => setSettings(data)); }, []);

    // Load SV C807 departments — robusto con retry y manejo de errores.
    // Se ejecuta cuando country=SV (independiente de needsShipping) para que
    // el dropdown esté listo cuando el usuario active envío.
    const loadDepartamentos = useCallback(async () => {
        if (country !== "SV") return;
        setLoadingDeptos(true);
        try {
            const { data } = await api.get("/c807-public/catalogo/departamentos");
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setDepartamentos(list);
            if (list.length === 0) console.warn("[checkout] departamentos vino vacío");
        } catch (err) {
            console.warn("[checkout] error cargando departamentos:", err?.message);
            setDepartamentos([]);
        } finally { setLoadingDeptos(false); }
    }, [country]);
    useEffect(() => {
        if (country === "SV" && departamentos.length === 0 && !loadingDeptos) {
            loadDepartamentos();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country]);

    const onPickDepartamento = async (depIdRaw) => {
        const depId = depIdRaw === "" || depIdRaw == null ? "" : Number(depIdRaw);
        setForm((f) => ({ ...f, departamento_id: depId, municipio_id: "" }));
        setMunicipios([]);
        if (!depId || Number.isNaN(depId)) return;
        setLoadingMunicipios(true);
        try {
            const { data } = await api.get(`/c807-public/catalogo/municipios?departamento=${depId}`);
            const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setMunicipios(list);
            if (list.length === 0) toast.warning("No se encontraron distritos para este departamento. Reintenta.");
        } catch (err) {
            console.warn("[checkout] error cargando municipios:", err?.message);
            setMunicipios([]);
            toast.error(t("No se pudo cargar la lista de distritos. Intenta de nuevo.", "Could not load the list of districts. Please try again."));
        } finally { setLoadingMunicipios(false); }
    };

    useEffect(() => {
        if (loading) return;
        if (!user) { nav("/login?next=/checkout"); return; }
        if (user.name) {
            const parts = user.name.split(" ");
            setForm((f) => ({ ...f, customer_name: parts[0] || "", customer_lastname: parts.slice(1).join(" ") || "" }));
        }
    }, [user, loading, nav]);

    // Auto-switch payment method if current one becomes disabled
    useEffect(() => {
        const pmFlagsX = settings.payment_methods || {};
        const allKeys = { apartado: "cash", stripe: "stripe", paypal: "paypal", transferencia: "transferencia", cce: "cce" };
        const isUsCtx = country === "US";
        const isCceCtx = !isUsCtx && needsShipping && tipoServicio === "CCE";
        const allowedForUs = ["stripe", "paypal"];
        if (isCceCtx) {
            if (method !== "cce") setMethod("cce");
            return;
        }
        if (method === "cce" && !isCceCtx) {
            setMethod("apartado");
            return;
        }
        const currentDisabled = pmFlagsX[allKeys[method]] === false;
        const currentNotUs = isUsCtx && !allowedForUs.includes(method);
        if (currentDisabled || currentNotUs) {
            const ids = isUsCtx ? allowedForUs : ["apartado", "stripe", "paypal", "transferencia"];
            const firstAvailable = ids.find((id) => pmFlagsX[allKeys[id]] !== false);
            if (firstAvailable) setMethod(firstAvailable);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.payment_methods, country, needsShipping, tipoServicio]);

    // Re-validate discount when subtotal/country change (in case user adds items / switches country)
    useEffect(() => {
        if (!discountApplied) return;
        const skus = items.map((i) => i.sku).filter(Boolean);
        api.post("/discount-codes/validate", { code: discountApplied.code, subtotal: total, country, skus })
            .then(({ data }) => setDiscountApplied(data))
            .catch(() => { setDiscountApplied(null); toast.warning("El cupón ya no aplica con tu carrito actual."); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [total, country]);

    if (loading || !user) return <div className="p-12 text-center text-[var(--tb-text-2)]">Cargando...</div>;
    if (items.length === 0) return <div className="p-12 text-center">{t("Carrito vacío", "Empty cart")}</div>;

    const isUS = country === "US";
    const isCCE = !isUS && needsShipping && tipoServicio === "CCE";
    // Country-specific store address (falls back to legacy `address` field)
    const storeAddress = isUS
        ? (settings.address_us || settings.address || "Envíos a todo Estados Unidos vía USPS")
        : (settings.address_sv || settings.address || "nuestra tienda física");

    // USA: only stripe/paypal allowed. SV: all enabled methods. CCE: only "pago contra entrega".
    const allMethods = [
        { id: "apartado", key: "cash", label: "Efectivo en Tienda", icon: Store, desc: "Reservamos el producto. Págalo en efectivo al recogerlo en: " + storeAddress },
        { id: "stripe", key: "stripe", label: "Tarjeta (Stripe)", icon: CreditCard, desc: "Pago seguro con tarjeta Visa/MasterCard." },
        { id: "paypal", key: "paypal", label: "PayPal", icon: Wallet, desc: "Envía el pago a: " + (settings.paypal_email || "—") },
        { id: "transferencia", key: "transferencia", label: "Transferencia Bancaria", icon: ArrowRightLeft, desc: settings.bank_info || "Datos bancarios disponibles tras confirmar." },
        { id: "cce", key: "cce", label: "Pago contra entrega (CCE)", icon: Truck, desc: "Pagas al mensajero C807 cuando recibas tu paquete. NO se cobra nada ahora." },
    ];
    const pmFlags = settings.payment_methods || {};
    let methods = allMethods.filter((m) => m.key === "cce" || pmFlags[m.key] !== false);
    if (isUS) methods = methods.filter((m) => m.key === "stripe" || m.key === "paypal");
    if (isCCE) methods = methods.filter((m) => m.key === "cce");
    else methods = methods.filter((m) => m.key !== "cce");
    if (methods.length === 0) return <div className="p-12 text-center text-rose-400">No hay métodos de pago habilitados. Contacta al administrador.</div>;

    // SV-only: apartado y CCE solo disponibles para usuarios verificados (check azul).
    const isVerified = !!user?.verified;
    const requiresVerification = (mid) => !isUS && (mid === "apartado" || mid === "cce") && !isVerified;

    // Shipping logic — usa el sistema de niveles inverso (admin lo configura por país)
    const effectiveShipping = isUS ? true : (method === "apartado" ? false : needsShipping);
    const computeTierShipping = (active, tiers, subtotal) => {
        if (!active || !Array.isArray(tiers) || tiers.length === 0) return 0;
        const sorted = [...tiers].sort((a, b) => Number(a.min_subtotal || 0) - Number(b.min_subtotal || 0));
        for (const t of sorted) {
            const minV = Number(t.min_subtotal || 0);
            const maxRaw = t.max_subtotal;
            const maxV = (maxRaw == null || maxRaw === "" || Number(maxRaw) === 0) ? null : Number(maxRaw);
            if (subtotal >= minV && (maxV === null || subtotal < maxV)) return Number(t.cost || 0);
        }
        return 0;
    };
    let shipping = 0;
    let freeShippingEarned = false;
    if (isUS) {
        shipping = computeTierShipping(settings.us_shipping_tiers_active, settings.us_shipping_tiers, total);
        freeShippingEarned = shipping === 0;
    } else if (effectiveShipping && !isCCE) {
        shipping = computeTierShipping(settings.sv_shipping_tiers_active, settings.sv_shipping_tiers, total);
        freeShippingEarned = shipping === 0;
    }
    const grandTotal = Math.max(0, total + shipping - (discountApplied?.amount_off || 0));
    // Wallet credit applies AFTER discount and is capped by both balance and grandTotal.
    const walletApplied = useWalletCredit ? Math.min(walletBalance, grandTotal) : 0;
    const finalTotal = Math.max(0, grandTotal - walletApplied);
    // Cuánto falta para que el siguiente nivel sea más barato (o gratis)
    const tiersForCountry = isUS ? settings.us_shipping_tiers : settings.sv_shipping_tiers;
    const tiersActiveForCountry = isUS ? settings.us_shipping_tiers_active : settings.sv_shipping_tiers_active;
    let nextTierHint = null; // { amount, cost }
    if (effectiveShipping && tiersActiveForCountry && Array.isArray(tiersForCountry)) {
        const sorted = [...tiersForCountry].sort((a, b) => Number(a.min_subtotal || 0) - Number(b.min_subtotal || 0));
        for (const t of sorted) {
            const minV = Number(t.min_subtotal || 0);
            const cost = Number(t.cost || 0);
            if (minV > total && cost < shipping) {
                nextTierHint = { amount: minV - total, cost };
                break;
            }
        }
    }

    // Discount code handlers
    const applyDiscount = async () => {
        const code = (discountInput || "").trim().toUpperCase();
        if (!code) { toast.error(t("Ingresa un código", "Enter a code")); return; }
        setDiscountChecking(true);
        try {
            const skus = items.map((i) => i.sku).filter(Boolean);
            const { data } = await api.post("/discount-codes/validate", { code, subtotal: total, country, skus });
            setDiscountApplied(data);
            toast.success(t(`Cupón ${data.code} aplicado: -${data.type === "percent" ? `${data.value}%` : `$${data.amount_off.toFixed(2)}`}`, `Coupon ${data.code} applied: -${data.type === "percent" ? `${data.value}%` : `$${data.amount_off.toFixed(2)}`}`));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("Código no válido", "Invalid code"));
            setDiscountApplied(null);
        } finally { setDiscountChecking(false); }
    };
    const clearDiscount = () => { setDiscountApplied(null); setDiscountInput(""); };

    // Partner code handlers (afiliado / socio) — no descuento, solo tracking.
    const applyPartner = async () => {
        const code = (partnerInput || "").trim().toUpperCase();
        if (!code) { toast.error(t("Ingresa un código de socio", "Enter a partner code")); return; }
        setPartnerChecking(true);
        try {
            // Pass the current country so the backend can enforce program
            // isolation (SV code cannot be used on US checkout and vice versa).
            const { data } = await api.post("/partners/validate", { code, country });
            setPartnerApplied(data);
            toast.success(t(`Código de socio válido: ${data.display_name}`, `Valid partner code: ${data.display_name}`));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("Código de socio inválido", "Invalid partner code"));
            setPartnerApplied(null);
        } finally { setPartnerChecking(false); }
    };
    const clearPartner = () => { setPartnerApplied(null); setPartnerInput(""); };


    // Format the assembled US address into a single shipping_address string.
    const composeUsAddress = () => {
        const parts = [
            form.us_street,
            form.us_apt ? `Apt/Suite ${form.us_apt}` : "",
            `${form.us_city}, ${form.us_state} ${form.us_zip}`,
        ].filter(Boolean);
        return parts.join("\n");
    };

    const composeSvAddress = () => {
        // Build a human-readable address string from the structured SV fields.
        const depName = (departamentos.find((d) => String(d.id) === String(form.departamento_id)) || {}).nombre || "";
        const muniName = (municipios.find((m) => String(m.id) === String(form.municipio_id)) || {}).nombre || "";
        const parts = [form.sv_street.trim(), muniName, depName, "El Salvador"].filter(Boolean);
        let str = parts.join(", ");
        if (form.sv_reference.trim()) str += `\nReferencia: ${form.sv_reference.trim()}`;
        return str;
    };

    const submit = async () => {
        if (!form.customer_name.trim() || !form.customer_lastname.trim()) {
            toast.error(t("Nombre y apellido son requeridos", "First and last name are required")); return;
        }
        if (isUS) {
            if (!form.us_street.trim() || !form.us_city.trim() || form.us_state.length !== 2 || !form.us_zip.trim()) {
                toast.error(t("Completa Calle, Ciudad, Estado (2 letras) y ZIP", "Please complete Street, City, State (2 letters) and ZIP")); return;
            }
        } else if (effectiveShipping) {
            if (!form.sv_street.trim()) { toast.error(t("Ingresa la calle y número de casa", "Enter the street and house number")); return; }
            if (!form.departamento_id) { toast.error(t("Selecciona el departamento", "Select the department")); return; }
            if (!form.municipio_id) { toast.error(t("Selecciona el municipio / distrito", "Select the municipality / district")); return; }
        }
        setLoadingSubmit(true);
        try {
            const payloadAddress = isUS ? composeUsAddress() : (effectiveShipping ? composeSvAddress() : "");
            const { data } = await api.post("/orders", {
                items: items.map((i) => ({ sku: i.sku, quantity: i.quantity })),
                payment_method: method,
                customer_name: form.customer_name,
                customer_lastname: form.customer_lastname,
                customer_phone: form.customer_phone,
                shipping_address: payloadAddress,
                needs_shipping: effectiveShipping,
                notes: form.notes,
                origin_url: window.location.origin,
                country,
                us_state: isUS ? form.us_state : undefined,
                us_zip: isUS ? form.us_zip : undefined,
                us_city: isUS ? form.us_city : undefined,
                // SV courier (C807) extras
                tipo_servicio: !isUS && effectiveShipping ? tipoServicio : undefined,
                departamento_id: !isUS && effectiveShipping ? form.departamento_id : undefined,
                municipio_id: !isUS && effectiveShipping ? form.municipio_id : undefined,
                // SV structured address fields (persisted en pedido para mostrar luego)
                sv_street: !isUS && effectiveShipping ? form.sv_street : undefined,
                sv_reference: !isUS && effectiveShipping ? form.sv_reference : undefined,
                // SV GPS Plus Code (Open Location Code) — optional, complements the written address
                sv_plus_code: !isUS && effectiveShipping ? (form.location?.plus_code || null) : undefined,
                sv_plus_code_full: !isUS && effectiveShipping ? (form.location?.plus_code_full || null) : undefined,
                sv_plus_code_human: !isUS && effectiveShipping ? (form.location?.plus_code_human || null) : undefined,
                sv_maps_url: !isUS && effectiveShipping ? (form.location?.maps_url || null) : undefined,
                sv_lat: !isUS && effectiveShipping ? (form.location?.lat ?? null) : undefined,
                sv_lng: !isUS && effectiveShipping ? (form.location?.lng ?? null) : undefined,
                discount_code: discountApplied?.code || undefined,
                partner_code: partnerApplied?.code || undefined,
                use_wallet_credit: walletApplied > 0 ? walletApplied : undefined,
            });
            if (method === "stripe" && data.stripe_url) {
                clear();
                window.location.href = data.stripe_url;
                return;
            }
            if (method === "paypal") {
                try {
                    const { data: pp } = await api.post(`/paypal/create-order/${data.order_id}`);
                    if (pp.approve_url) {
                        clear();
                        window.location.href = pp.approve_url;
                        return;
                    }
                } catch (err) {
                    toast.error("PayPal no disponible: " + formatApiErrorDetail(err.response?.data?.detail));
                    setLoadingSubmit(false);
                    return;
                }
            }
            clear();
            try {
                const fullOrder = await api.get(`/orders/${data.order_id}`).then((r) => r.data);
                await generateReceipt(fullOrder, settings, { withQR: method !== "transferencia" });
                toast.success(`Pedido enviado. Recibo descargado. ID: ${data.order_id.slice(0, 8)}`);
            } catch {
                toast.success(`Pedido enviado al administrador. ID: ${data.order_id.slice(0, 8)}`);
            }
            nav(`/perfil?tab=ordenes`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoadingSubmit(false); }
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-12 fade-in">
            <h1 className="font-display text-4xl mb-2">{t("Finalizar compra", "Checkout")}</h1>
            <p className="text-[var(--tb-text-2)] mb-8">{t("Confirma tus datos. El administrador revisará y aprobará tu pedido.", "Confirm your details. The admin will review and approve your order.")}</p>
            <div className="grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-6">
                    {/* Customer data */}
                    <div className="tb-card p-6">
                        <div className="tb-overline text-[var(--tb-gold)] mb-4 flex items-center gap-2"><User className="w-4 h-4" /> Datos del cliente</div>
                        <div className="grid grid-cols-2 gap-3">
                            <label><span className="tb-overline">Nombre *</span><input className="tb-input mt-1" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="checkout-nombre" /></label>
                            <label><span className="tb-overline">Apellido *</span><input className="tb-input mt-1" value={form.customer_lastname} onChange={(e) => setForm({ ...form, customer_lastname: e.target.value })} required data-testid="checkout-apellido" /></label>
                            <label className="col-span-2"><span className="tb-overline">Teléfono / WhatsApp *</span><input className="tb-input mt-1" placeholder={isUS ? "+1 555-555-5555" : "+503 7000-0000"} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="checkout-phone" /></label>
                        </div>
                    </div>

                    {/* Delivery */}
                    <div className="tb-card p-6">
                        <div className="tb-overline text-[var(--tb-gold)] mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" /> {t("Entrega", "Delivery")}</div>
                        <div className="space-y-3">
                            {isUS ? (
                                <>
                                    <div className="p-3 border border-[var(--tb-gold)]/40 bg-[var(--tb-border-gold)]/10 rounded">
                                        <div className="font-medium flex items-center gap-2"><Truck className="w-4 h-4 text-[var(--tb-gold)]" /> Envío a Estados Unidos</div>
                                        <div className="text-xs text-[var(--tb-text-2)] mt-1">Solo envío terrestre USPS (productos hazmat). No hay retiro en tienda para clientes USA.</div>
                                    </div>
                                    {/* Structured US address */}
                                    <div className="grid grid-cols-2 gap-3" data-testid="us-address-block">
                                        <label className="col-span-2 text-xs">Calle y número *
                                            <input className="tb-input mt-1" placeholder="123 Main Street" value={form.us_street} onChange={(e) => setForm({ ...form, us_street: e.target.value })} data-testid="us-street" />
                                        </label>
                                        <label className="col-span-2 text-xs">Apartamento / Suite (opcional)
                                            <input className="tb-input mt-1" placeholder="Apt 4B" value={form.us_apt} onChange={(e) => setForm({ ...form, us_apt: e.target.value })} data-testid="us-apt" />
                                        </label>
                                        <label className="text-xs">Ciudad *
                                            <input className="tb-input mt-1" placeholder="Los Angeles" value={form.us_city} onChange={(e) => setForm({ ...form, us_city: e.target.value })} data-testid="us-city" />
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <label className="text-xs">Estado *
                                                <input className="tb-input mt-1 uppercase" maxLength={2} placeholder="CA" value={form.us_state} onChange={(e) => setForm({ ...form, us_state: e.target.value.toUpperCase() })} data-testid="us-state" />
                                            </label>
                                            <label className="text-xs">ZIP *
                                                <input className="tb-input mt-1" placeholder="90001" value={form.us_zip} onChange={(e) => setForm({ ...form, us_zip: e.target.value })} data-testid="us-zip" />
                                            </label>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <label className={`flex items-start gap-3 p-3 border cursor-pointer ${!needsShipping ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/20" : "border-[var(--tb-border)]"}`}>
                                        <input type="radio" checked={!needsShipping} onChange={() => setNeedsShipping(false)} className="mt-1" data-testid="delivery-pickup" />
                                        <div className="flex-1">
                                            <div className="font-medium">{t("Retiro en tienda", "Store pickup")}</div>
                                            <div className="text-xs text-[var(--tb-text-2)] mt-1 whitespace-pre-line">{settings.address_sv || settings.address || "Sonsonate, El Salvador"}</div>
                                            {/* Clickable Google Maps link — opens Maps app on mobile, browser on desktop. */}
                                            <a
                                                href={settings.address_sv_maps_url || "https://maps.app.goo.gl/xzn42YAPropaHAFx5"}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="inline-flex items-center gap-1.5 mt-2 text-xs text-[var(--tb-gold)] hover:text-[var(--tb-gold-hover)] underline underline-offset-2 decoration-[var(--tb-gold)]/40 hover:decoration-[var(--tb-gold)]"
                                                data-testid="pickup-maps-link">
                                                <MapPin className="w-3.5 h-3.5" />
                                                Ver en Google Maps
                                            </a>
                                        </div>
                                    </label>
                                    <label className={`flex items-start gap-3 p-3 border cursor-pointer ${needsShipping && method !== "apartado" ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/20" : "border-[var(--tb-border)]"} ${method === "apartado" ? "opacity-50 cursor-not-allowed" : ""}`}>
                                        <input type="radio" checked={needsShipping && method !== "apartado"} onChange={() => setNeedsShipping(true)} disabled={method === "apartado"} className="mt-1" data-testid="delivery-ship" />
                                        <div className="flex-1">
                                            <div className="font-medium">{t("Envío a domicilio", "Home delivery")}</div>
                                            <div className="text-xs text-[var(--tb-text-2)] mt-1">{method === "apartado" ? t("No disponible para apartado", "Not available for layaway") : `${t("Costo dinámico según el subtotal", "Dynamic cost based on subtotal")} · ${shipping === 0 ? t("gratis ahora", "free now") : `${t("actual", "current")} ${formatPrice(shipping)}`}`}</div>
                                        </div>
                                    </label>
                                    {effectiveShipping && (
                                        <>
                                            {/* Tipo de servicio C807: SER vs CCE — stacked on mobile so long text never overflows */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid="ser-cce-selector">
                                                <label className={`p-3 border cursor-pointer text-xs transition min-w-0 ${tipoServicio === "SER" ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/15" : "border-[var(--tb-border)]"}`}>
                                                    <input type="radio" name="tipo_servicio" checked={tipoServicio === "SER"} onChange={() => setTipoServicio("SER")} className="mr-2 align-middle" data-testid="tipo-ser" />
                                                    <strong>SER · Entrega Regular</strong>
                                                    <div className="text-[10px] text-[var(--tb-text-2)] mt-1 break-words">Pagas en el sitio (tarjeta/PayPal/transferencia/apartado). Envío {shipping === 0 ? "gratis" : formatPrice(shipping)}.</div>
                                                </label>
                                                <label className={`p-3 border text-xs transition min-w-0 ${isVerified ? "cursor-pointer" : "cursor-not-allowed opacity-60"} ${tipoServicio === "CCE" && isVerified ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/15" : "border-[var(--tb-border)]"}`}>
                                                    <input type="radio" name="tipo_servicio" checked={tipoServicio === "CCE"} disabled={!isVerified}
                                                        onChange={() => { if (isVerified) setTipoServicio("CCE"); }} className="mr-2 align-middle" data-testid="tipo-cce" />
                                                    <strong>CCE · Pago contra entrega</strong>
                                                    {!isVerified && (
                                                        <span className="ml-2 text-[10px] tb-badge tb-badge-warning inline-flex items-center gap-1" data-testid="tipo-cce-locked">
                                                            <BadgeCheck className="w-3 h-3" /> Solo verificados
                                                        </span>
                                                    )}
                                                    <div className="text-[10px] text-[var(--tb-text-2)] mt-1 break-words">NO pagas nada ahora. El mensajero cobra {formatPrice(total)} cuando entregue.</div>
                                                    {!isVerified && (
                                                        <Link to="/perfil?tab=verificacion" onClick={(e) => e.stopPropagation()}
                                                            className="text-[10px] text-[#2196F3] hover:underline inline-flex items-center gap-1 mt-1"
                                                            data-testid="tipo-cce-verify-cta">
                                                            <BadgeCheck className="w-3 h-3" /> Verificarme para desbloquear CCE
                                                        </Link>
                                                    )}
                                                </label>
                                            </div>

                                            <label><span className="tb-overline">Calle y número de casa *</span>
                                                <input className="tb-input mt-1" placeholder="Ej. Calle Principal #123"
                                                    value={form.sv_street}
                                                    onChange={(e) => setForm({ ...form, sv_street: e.target.value })}
                                                    data-testid="checkout-sv-street" />
                                            </label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <label className="text-xs">Departamento *
                                                    <div className="relative">
                                                        <select className="tb-input mt-1"
                                                            value={form.departamento_id === "" ? "" : String(form.departamento_id)}
                                                            onChange={(e) => onPickDepartamento(e.target.value)}
                                                            disabled={loadingDeptos}
                                                            data-testid="checkout-departamento">
                                                            <option value="">{loadingDeptos ? "Cargando..." : (departamentos.length === 0 ? "Sin opciones — reintenta" : "Selecciona...")}</option>
                                                            {departamentos.map((d) => <option key={d.id} value={String(d.id)}>{d.nombre}</option>)}
                                                        </select>
                                                        {departamentos.length === 0 && !loadingDeptos && (
                                                            <button type="button" onClick={loadDepartamentos}
                                                                className="absolute -top-5 right-0 text-[10px] text-[var(--tb-gold)] hover:underline"
                                                                data-testid="reload-departamentos">↻ Reintentar</button>
                                                        )}
                                                    </div>
                                                </label>
                                                <label className="text-xs">Municipio / Distrito *
                                                    <select className="tb-input mt-1"
                                                        value={form.municipio_id === "" ? "" : String(form.municipio_id)}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setForm({ ...form, municipio_id: v === "" ? "" : Number(v) });
                                                        }}
                                                        disabled={loadingMunicipios || !form.departamento_id || municipios.length === 0}
                                                        data-testid="checkout-municipio">
                                                        <option value="">
                                                            {!form.departamento_id ? "Elige departamento"
                                                                : loadingMunicipios ? "Cargando..."
                                                                : municipios.length === 0 ? "Sin distritos — reintenta"
                                                                : "Selecciona..."}
                                                        </option>
                                                        {municipios.map((m) => <option key={m.id} value={String(m.id)}>{m.nombre}</option>)}
                                                    </select>
                                                </label>
                                            </div>
                                            <label><span className="tb-overline">Puntos de referencia <span className="text-[var(--tb-muted)] normal-case tracking-normal">(opcional)</span></span>
                                                <textarea className="tb-input mt-1 min-h-[60px]"
                                                    placeholder="Ej. Frente al parque central · Casa color azul con portón blanco"
                                                    value={form.sv_reference}
                                                    onChange={(e) => setForm({ ...form, sv_reference: e.target.value })}
                                                    data-testid="checkout-sv-reference" />
                                            </label>
                                            {/* Plus Code location picker — SV only, optional but strongly recommended */}
                                            <LocationPicker
                                                value={form.location}
                                                onChange={(loc) => setForm({ ...form, location: loc })}
                                                departamentoName={(departamentos.find((d) => String(d.id) === String(form.departamento_id)) || {}).nombre || ""}
                                                municipioName={(municipios.find((m) => String(m.id) === String(form.municipio_id)) || {}).nombre || ""}
                                                centerHint={centroidForDepartamento(form.departamento_id)}
                                            />
                                        </>
                                    )}
                                    {effectiveShipping && settings.shipping_partner?.enabled && settings.shipping_partner?.company_name && (
                                        <div className="mt-2 p-3 border border-[var(--tb-border)] bg-[var(--tb-bg-2)]/40 text-xs flex items-start gap-3" data-testid="checkout-courier-info">
                                            {settings.shipping_partner.logo_url && (
                                                <img src={settings.shipping_partner.logo_url} alt={settings.shipping_partner.company_name} className="h-8 w-auto object-contain shrink-0 mt-0.5" />
                                            )}
                                            <div className="flex-1 space-y-0.5">
                                                <div className="text-[var(--tb-text)]">
                                                    Entregado por <span className="font-medium">{settings.shipping_partner.company_name}</span>
                                                    {settings.shipping_partner.service_type ? ` · ${settings.shipping_partner.service_type}` : ""}
                                                </div>
                                                {settings.shipping_partner.delivery_time && (
                                                    <div className="text-[var(--tb-muted)]">⏱ {settings.shipping_partner.delivery_time}</div>
                                                )}
                                                {settings.shipping_partner.coverage_areas && (
                                                    <div className="text-[var(--tb-muted)]">📍 {settings.shipping_partner.coverage_areas}</div>
                                                )}
                                                {settings.shipping_partner.notes && (
                                                    <div className="text-[var(--tb-muted)] italic mt-1">{settings.shipping_partner.notes}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Payment */}
                    <div className="tb-card p-6">
                        <div className="tb-overline text-[var(--tb-gold)] mb-4">{t("Método de pago", "Payment method")}</div>
                        <div className="space-y-3">
                            {methods.map((m) => {
                                const locked = requiresVerification(m.id);
                                return (
                                    <label key={m.id}
                                        className={`flex items-start gap-4 p-4 border transition-all ${locked ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${method === m.id && !locked ? "border-[var(--tb-gold)] bg-[var(--tb-border-gold)]/20" : "border-[var(--tb-border)]"}`}
                                        data-testid={`payment-${m.id}`}>
                                        <input type="radio" checked={method === m.id} disabled={locked}
                                            onChange={() => { if (locked) return; setMethod(m.id); if (m.id === "apartado") setNeedsShipping(false); }} className="mt-1" />
                                        <m.icon className="w-5 h-5 text-[var(--tb-gold)] mt-0.5" />
                                        <div className="flex-1">
                                            <div className="font-medium flex items-center gap-2 flex-wrap">
                                                <span>{m.label}</span>
                                                {locked && (
                                                    <span className="text-[10px] tb-badge tb-badge-warning inline-flex items-center gap-1" data-testid={`payment-${m.id}-locked`}>
                                                        <BadgeCheck className="w-3 h-3" /> Solo verificados
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-[var(--tb-text-2)] mt-1">{m.desc}</div>
                                            {locked && (
                                                <Link to="/perfil?tab=verificacion"
                                                    className="text-[11px] text-[#2196F3] hover:underline inline-flex items-center gap-1 mt-2"
                                                    data-testid={`payment-${m.id}-verify-cta`}>
                                                    <BadgeCheck className="w-3 h-3" /> Verificarme para desbloquear este método
                                                </Link>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="tb-card p-6">
                        <div className="tb-overline text-[var(--tb-gold)] mb-3">{t("Notas para el administrador", "Notes for admin")}</div>
                        <textarea className="tb-input min-h-[80px]" placeholder={t("Talla, color preferido, instrucciones especiales...", "Size, preferred color, special instructions...")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" />
                    </div>
                </div>

                {/* Summary */}
                <div className="tb-card p-6 h-fit md:sticky md:top-24" data-testid="checkout-summary">
                    <div className="tb-overline text-[var(--tb-gold)] mb-4 flex items-center justify-between">
                        <span>{t("Tu pedido", "Your order")} ({items.reduce((s, i) => s + i.quantity, 0)} {items.reduce((s, i) => s + i.quantity, 0) === 1 ? t("artículo", "item") : t("artículos", "items")})</span>
                    </div>
                    <div className="space-y-3 mb-3" data-testid="checkout-items">
                        {items.map((i) => (
                            <div key={i.sku} className="flex items-start gap-3" data-testid={`checkout-item-${i.sku}`}>
                                {(() => { const s = productImage(i); return s ? <img src={s} alt="" className="w-14 h-14 rounded object-cover flex-shrink-0" /> : null; })()}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm leading-tight truncate">{i.nombre}</div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">{i.sku} · {formatPrice(i.precio)} c/u</div>
                                    <div className="flex items-center gap-1 mt-1.5">
                                        <button
                                            type="button"
                                            onClick={() => (i.quantity <= 1 ? remove(i.sku) : setQty(i.sku, i.quantity - 1))}
                                            className="w-6 h-6 rounded border border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:text-[var(--tb-gold)] text-xs inline-flex items-center justify-center transition"
                                            aria-label="Reducir cantidad"
                                            data-testid={`checkout-qty-minus-${i.sku}`}
                                        >−</button>
                                        <span className="min-w-[24px] text-center text-sm font-medium" data-testid={`checkout-qty-value-${i.sku}`}>{i.quantity}</span>
                                        <button
                                            type="button"
                                            onClick={() => setQty(i.sku, i.quantity + 1)}
                                            className="w-6 h-6 rounded border border-[var(--tb-border)] hover:border-[var(--tb-gold)] hover:text-[var(--tb-gold)] text-xs inline-flex items-center justify-center transition"
                                            aria-label="Aumentar cantidad"
                                            data-testid={`checkout-qty-plus-${i.sku}`}
                                        >+</button>
                                        <button
                                            type="button"
                                            onClick={() => remove(i.sku)}
                                            className="ml-2 text-[var(--tb-muted)] hover:text-rose-400 transition"
                                            aria-label="Eliminar producto"
                                            title="Eliminar del pedido"
                                            data-testid={`checkout-remove-${i.sku}`}
                                        ><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                                <div className="text-sm text-[var(--tb-gold)] font-medium tabular-nums flex-shrink-0">{formatPrice(i.precio * i.quantity)}</div>
                            </div>
                        ))}
                    </div>

                    {/* Discount code section */}
                    <div className="border-t border-[var(--tb-border)] mt-3 pt-3" data-testid="discount-code-block">
                        {discountApplied ? (
                            <div className="flex items-center justify-between text-sm bg-[var(--tb-border-gold)]/15 border border-[var(--tb-gold)]/40 rounded px-3 py-2" data-testid="discount-applied">
                                <div className="flex items-center gap-2 min-w-0">
                                    <BadgeCheck className="w-4 h-4 text-[var(--tb-gold)] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-[var(--tb-gold)] truncate">{discountApplied.code}</div>
                                        <div className="text-[10px] text-[var(--tb-text-2)] truncate">
                                            {discountApplied.type === "percent" ? `${discountApplied.value}% off` : `$${discountApplied.amount_off.toFixed(2)} off`}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={clearDiscount} className="text-[var(--tb-muted)] hover:text-rose-400 shrink-0" data-testid="discount-clear" title="Quitar cupón">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <details className="text-xs">
                                <summary className="cursor-pointer text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] transition select-none flex items-center gap-1.5" data-testid="discount-code-toggle">
                                    <BadgeCheck className="w-3.5 h-3.5" /> ¿Tienes un código de descuento?
                                </summary>
                                <div className="mt-2 flex gap-2">
                                    <input
                                        className="tb-input !py-1.5 !text-xs uppercase font-mono"
                                        placeholder="EJ: VERANO2026"
                                        value={discountInput}
                                        onChange={(e) => setDiscountInput(e.target.value.toUpperCase().replace(/\s/g, ""))}
                                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyDiscount())}
                                        data-testid="discount-input"
                                    />
                                    <button
                                        onClick={applyDiscount}
                                        disabled={discountChecking || !discountInput.trim()}
                                        className="tb-btn-secondary !text-xs disabled:opacity-50 shrink-0"
                                        data-testid="discount-apply"
                                    >
                                        {discountChecking ? "..." : "Aplicar"}
                                    </button>
                                </div>
                            </details>
                        )}
                    </div>

                    {/* Partner code section (afiliado / socio) — no descuento, solo tracking */}
                    <div className="border-t border-[var(--tb-border)] mt-3 pt-3" data-testid="partner-code-block">
                        {partnerApplied ? (
                            <div className="flex items-center justify-between text-sm bg-emerald-500/10 border border-emerald-400/40 rounded px-3 py-2" data-testid="partner-applied">
                                <div className="flex items-center gap-2 min-w-0">
                                    {partnerApplied.photo ? (
                                        <img src={partnerApplied.photo} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                                    ) : (
                                        <BadgeCheck className="w-4 h-4 text-emerald-300 shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-emerald-300 truncate">{partnerApplied.code}</div>
                                        <div className="text-[10px] text-[var(--tb-text-2)] truncate">
                                            {t("Recomendado por", "Referred by")} <span className="text-[var(--tb-gold)]">{partnerApplied.display_name}</span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={clearPartner} className="text-[var(--tb-muted)] hover:text-rose-400 shrink-0" data-testid="partner-clear" title={t("Quitar código", "Remove code")}>
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <details className="text-xs">
                                <summary className="cursor-pointer text-[var(--tb-text-2)] hover:text-[var(--tb-gold)] transition select-none flex items-center gap-1.5" data-testid="partner-code-toggle">
                                    <BadgeCheck className="w-3.5 h-3.5" /> {t("¿Tienes un código de socio? (no aplica descuento)", "Do you have a partner code? (no discount)")}
                                </summary>
                                <div className="mt-2 flex gap-2">
                                    <input
                                        className="tb-input !py-1.5 !text-xs uppercase font-mono"
                                        placeholder={t("EJ: JULIO25", "E.G. JULIO25")}
                                        value={partnerInput}
                                        onChange={(e) => setPartnerInput(e.target.value.toUpperCase().replace(/\s/g, ""))}
                                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyPartner())}
                                        data-testid="partner-input"
                                    />
                                    <button
                                        onClick={applyPartner}
                                        disabled={partnerChecking || !partnerInput.trim()}
                                        className="tb-btn-secondary !text-xs disabled:opacity-50 shrink-0"
                                        data-testid="partner-apply"
                                    >
                                        {partnerChecking ? "..." : t("Aplicar", "Apply")}
                                    </button>
                                </div>
                                <p className="text-[10px] text-[var(--tb-muted)] mt-1 leading-relaxed">
                                    {t("Los códigos de socio no aplican descuento; solo ayudan a reconocer a quien te recomendó Terra Boutique.", "Partner codes do not apply a discount; they just help us credit whoever referred you to Terra Boutique.")}
                                </p>
                            </details>
                        )}
                    </div>

                    <div className="border-t border-[var(--tb-border)] mt-3 pt-3 text-sm">
                        {siteDiscountAmount > 0 && (
                            <div className="flex justify-between mb-1 text-[var(--tb-muted)]">
                                <span>Subtotal original</span>
                                <span className="line-through">{formatPrice(originalTotal)}</span>
                            </div>
                        )}
                        {siteDiscountAmount > 0 && (
                            <div className="flex justify-between mb-1 text-[#4CAF50]" data-testid="site-discount-line">
                                <span>Promoción</span>
                                <span>-{formatPrice(siteDiscountAmount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between mb-1"><span>Subtotal</span><span>{formatPrice(total)}</span></div>
                        {discountApplied && (
                            <div className="flex justify-between mb-1 text-[var(--tb-gold)]" data-testid="discount-line">
                                <span>Descuento ({discountApplied.code})</span>
                                <span>-{formatPrice(discountApplied.amount_off)}</span>
                            </div>
                        )}
                        <div className="flex justify-between mb-1">
                            <span>Envío {isUS ? "(USPS Ground)" : ""}</span>
                            <span className={shipping === 0 && effectiveShipping ? "text-[var(--tb-gold)]" : ""}>
                                {!effectiveShipping ? "—" : freeShippingEarned ? "Gratis" : formatPrice(shipping)}
                            </span>
                        </div>
                        {/* Next tier hint (envío más barato/gratis si subes el subtotal) */}
                        {effectiveShipping && nextTierHint && (
                            <div className="text-[10px] text-[var(--tb-muted)] mt-1" data-testid="free-shipping-hint">
                                Agrega <span className="text-[var(--tb-gold)] font-medium">{formatPrice(nextTierHint.amount)}</span> más y tu envío {nextTierHint.cost === 0 ? "será gratis" : `bajará a ${formatPrice(nextTierHint.cost)}`}.
                            </div>
                        )}
                        {/* Wallet credit (saldo de sugerencias) */}
                        {walletBalance > 0 && (
                            <div className="mt-3 pt-3 border-t border-dashed border-[var(--tb-border)]" data-testid="wallet-credit-section">
                                <label className="flex items-center justify-between gap-3 cursor-pointer text-xs">
                                    <span className="flex items-center gap-2">
                                        <input type="checkbox" checked={useWalletCredit}
                                            onChange={(e) => setUseWalletCredit(e.target.checked)}
                                            data-testid="wallet-credit-toggle" />
                                        <span>Usar mi saldo de sugerencias</span>
                                    </span>
                                    <span className="text-[var(--tb-gold)] font-medium">{formatPrice(walletBalance)} disponible</span>
                                </label>
                                {useWalletCredit && walletApplied > 0 && (
                                    <div className="flex justify-between mt-1 text-[var(--tb-gold)]" data-testid="wallet-credit-line">
                                        <span>Crédito aplicado</span>
                                        <span>-{formatPrice(walletApplied)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="border-t border-[var(--tb-border)] mt-3 pt-3 flex justify-between text-xl">
                        <span>Total</span><span className="text-[var(--tb-gold)] font-semibold" data-testid="checkout-total">{formatPrice(finalTotal)}</span>
                    </div>
                    <div className="text-xs text-[var(--tb-muted)] mt-4 mb-4 leading-relaxed">
                        {t("Tu pedido se enviará al administrador para revisión. Te notificaremos cuando sea aprobado y listo para entrega/retiro.", "Your order will be sent to the admin for review. We'll notify you when it's approved and ready for pickup/delivery.")}
                    </div>
                    {siteMode !== "live" && user?.role !== "admin" ? (
                        <div className="space-y-2" data-testid="checkout-site-locked">
                            <button disabled className="tb-btn-primary w-full opacity-40 cursor-not-allowed">
                                {siteMode === "prelaunch" ? t("🚀 Compras disponibles en el lanzamiento", "🚀 Purchases available at launch") : t("🔧 Sitio en mantenimiento", "🔧 Site under maintenance")}
                            </button>
                            <div className="text-[11px] text-amber-400 text-center">
                                {siteMode === "prelaunch" ? t("Las compras estarán habilitadas el día del lanzamiento. Mientras tanto puedes seguir explorando.", "Purchases will be enabled on launch day. Meanwhile, feel free to keep browsing.") : t("Volveremos en unos minutos.", "We'll be back in a few minutes.")}
                            </div>
                        </div>
                    ) : (
                        <button disabled={loadingSubmit} onClick={submit} className="tb-btn-primary w-full disabled:opacity-50" data-testid="confirm-order-button">
                            {loadingSubmit ? t("Procesando...", "Processing...") : method === "stripe" ? t("Pagar con Stripe", "Pay with Stripe") : method === "paypal" ? t("Pagar con PayPal", "Pay with PayPal") : (method === "apartado" ? t("Confirmar apartado", "Confirm layaway") : t("Enviar pedido", "Send order"))}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
